import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  Box,
  InputAdornment,
  Popover,
  SxProps,
  Typography,
  Theme,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ReactComponent as SearchIcon } from '../../assets/search.svg';
import {
  OPTION_ITEM_HEIGHT,
  OPTION_ITEM_HEIGHT_WITH_DESCRIPTION,
} from '../DropDown/constants';
import { DropDownOption } from '../DropDown/types';
import { Flex } from '../Flex';
import TextField from '../TextField';
import { componentToken } from '../../style/utils';
import OptionItem from './components/OptionItem';
import SelectedItem from './components/SelectedItem';
import SelectAllItem from './components/SelectAllItem';

const SELECTED_HEADER_HEIGHT = 28;
const SHOW_MORE_BUTTON_HEIGHT = 32;
const DIVIDER_HEIGHT = 17;
const SELECT_ALL_HEIGHT = 44;
const DEFAULT_MAX_VISIBLE_SELECTED = 4;

type VirtualRow =
  | { type: 'selected-header'; count: number }
  | { type: 'selected-item'; option: DropDownOption }
  | { type: 'show-more-button'; totalCount: number; isExpanded: boolean }
  | { type: 'divider' }
  | { type: 'select-all'; checked: boolean; indeterminate: boolean }
  | { type: 'option'; option: DropDownOption; isSelected: boolean };

const PopoverStyled = styled(Popover)`
  .MuiPaper-root {
    padding: 0;
    width: 300px;
  }
`;

const Wrapper = styled(Flex)`
  flex-direction: column;
  max-height: 100%;
  width: 100%;
`;

const SearchFieldContainer = styled(Flex)`
  width: 100%;
  height: 44px;
  background: ${componentToken('filterChip.searchFieldContainer.background')};
  align-items: center;
  border-bottom: 1px solid
    ${componentToken('filterChip.searchFieldContainer.borderBottom')};
  flex-shrink: 0;
`;

const SearchIconFocused = styled(SearchIcon)`
  path {
    fill: ${componentToken('filterChip.searchIconFocused.pathFill')};
  }
`;

const ScrollableContainer = styled(Box)`
  overflow-y: auto;
  max-height: 300px;
  width: 100%;
`;

const SelectedSectionHeader = styled(Typography)`
  font-size: ${componentToken('filterChip.selectedSectionHeader.fontSize')};
  color: ${componentToken('filterChip.selectedSectionHeader.color')};
  padding: ${componentToken('filterChip.selectedSectionHeader.padding')};
  height: ${SELECTED_HEADER_HEIGHT}px;
`;

const ShowMoreButton = styled(Typography)`
  font-size: ${componentToken('filterChip.showMoreButton.fontSize')};
  color: ${componentToken('filterChip.showMoreButton.color')};
  padding: ${componentToken('filterChip.showMoreButton.padding')};
  cursor: pointer;
  height: ${SHOW_MORE_BUTTON_HEIGHT}px;

  &:hover {
    text-decoration: underline;
  }
`;

const DividerStyled = styled(Box)`
  background: ${componentToken('filterChip.divider.background')};
  height: 1px;
  width: 100%;
  margin: ${componentToken('filterChip.divider.margin')};
`;

const VirtualItemContainer = styled(Box)<{ $height: number; $offset: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${({ $height }) => $height}px;
  transform: translateY(${({ $offset }) => $offset}px);
`;

const PlaceholderBox = styled(Box)`
  height: 100px;
`;

const NoOptionsText = styled(Typography)`
  font-size: ${componentToken('filterChip.noOptionsText.fontSize')};
  text-align: center;
  padding: ${componentToken('filterChip.noOptionsText.padding')};
`;

export type OptionsMenuProps = {
  filteredOptions: DropDownOption[];
  handleItemClick: (value: string) => void;
  onSelectAllFilteredOptions?: (options: DropDownOption[]) => void;
  onDeselectAllFilteredOptions?: (options: DropDownOption[]) => void;
  setInitialSort?: React.Dispatch<React.SetStateAction<any>>;
  selectedItems: string | string[];
  indeterminate?: boolean;
  multiple?: boolean;
  showSelectAll?: boolean;
  showCounter?: boolean;
  optionRenderer?: (val: string) => string;
  emptyValueLabel?: string;
  sx?: SxProps<Theme>;
  showSearch?: boolean;
  anchorElement: HTMLButtonElement | null;
  setAnchorElement: (anchorElement: HTMLButtonElement | null) => void;
  showSelectedSection?: boolean;
  maxVisibleSelected?: number;
};

const OptionsMenu = ({
  showSearch = true,
  filteredOptions,
  handleItemClick,
  onSelectAllFilteredOptions,
  onDeselectAllFilteredOptions,
  selectedItems,
  anchorElement,
  setAnchorElement,
  showSelectedSection = false,
  maxVisibleSelected = DEFAULT_MAX_VISIBLE_SELECTED,
  optionRenderer,
  emptyValueLabel = 'N/A',
  indeterminate,
  multiple,
  showSelectAll,
  showCounter,
  setInitialSort,
}: OptionsMenuProps) => {
  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const [isSelectedExpanded, setIsSelectedExpanded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isOpen = !!anchorElement;

  const displayedOptions = useMemo(() => {
    if (!showSearch || !search) return filteredOptions;
    return filteredOptions.filter((o) =>
      o.label?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [filteredOptions, search, showSearch]);

  const selectedArray = useMemo(
    () => (Array.isArray(selectedItems) ? selectedItems : [selectedItems]),
    [selectedItems],
  );

  const selectedOptions = useMemo(() => {
    if (!showSelectedSection) return [];
    return displayedOptions.filter(
      (opt) => selectedArray.includes(opt.value) && opt.value !== 'all',
    );
  }, [showSelectedSection, displayedOptions, selectedArray]);

  const allDisplayedSelected = useMemo(() => {
    if (displayedOptions.length === 0) return false;
    return displayedOptions.every((opt) => selectedArray.includes(opt.value));
  }, [displayedOptions, selectedArray]);

  const virtualRows = useMemo(() => {
    const rows: VirtualRow[] = [];

    if (showSelectedSection && selectedOptions.length > 0) {
      rows.push({ type: 'selected-header', count: selectedOptions.length });

      const visibleSelected = isSelectedExpanded
        ? selectedOptions
        : selectedOptions.slice(0, maxVisibleSelected);

      visibleSelected.forEach((opt) =>
        rows.push({ type: 'selected-item', option: opt }),
      );

      if (selectedOptions.length > maxVisibleSelected) {
        rows.push({
          type: 'show-more-button',
          totalCount: selectedOptions.length,
          isExpanded: isSelectedExpanded,
        });
      }

      rows.push({ type: 'divider' });
    }

    if (showSelectAll) {
      rows.push({
        type: 'select-all',
        checked: allDisplayedSelected,
        indeterminate: indeterminate || false,
      });
      rows.push({ type: 'divider' });
    }

    displayedOptions.forEach((opt) =>
      rows.push({
        type: 'option',
        option: opt,
        isSelected: selectedArray.includes(opt.value),
      }),
    );

    return rows;
  }, [
    showSelectedSection,
    selectedOptions,
    isSelectedExpanded,
    maxVisibleSelected,
    showSelectAll,
    allDisplayedSelected,
    indeterminate,
    displayedOptions,
    selectedArray,
  ]);

  const getRowHeight = useCallback(
    (index: number) => {
      const row = virtualRows[index];
      switch (row.type) {
        case 'selected-header':
          return SELECTED_HEADER_HEIGHT;
        case 'selected-item':
          return OPTION_ITEM_HEIGHT;
        case 'show-more-button':
          return SHOW_MORE_BUTTON_HEIGHT;
        case 'divider':
          return DIVIDER_HEIGHT;
        case 'select-all':
          return SELECT_ALL_HEIGHT;
        case 'option':
          return row.option.description
            ? OPTION_ITEM_HEIGHT_WITH_DESCRIPTION
            : OPTION_ITEM_HEIGHT;
        default:
          return OPTION_ITEM_HEIGHT;
      }
    },
    [virtualRows],
  );

  const virtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: getRowHeight,
    overscan: 10,
  });

  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready, then set ready and remeasure
      const timer = setTimeout(() => {
        setIsReady(true);
        virtualizer.measure();
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setSearch('');
      setIsSelectedExpanded(false);
      setIsReady(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const virtualItems = virtualizer.getVirtualItems();

  const wrappedHandleItemClick = useCallback(
    (value: string) => {
      if (value === 'all') {
        if (allDisplayedSelected && onDeselectAllFilteredOptions) {
          onDeselectAllFilteredOptions(displayedOptions);
        } else if (onSelectAllFilteredOptions) {
          onSelectAllFilteredOptions(displayedOptions);
        }
      } else {
        handleItemClick(value);
      }
    },
    [
      handleItemClick,
      onSelectAllFilteredOptions,
      onDeselectAllFilteredOptions,
      displayedOptions,
      allDisplayedSelected,
    ],
  );

  const renderRow = useCallback(
    (row: VirtualRow, index: number) => {
      switch (row.type) {
        case 'selected-header':
          return (
            <SelectedSectionHeader key={`header-${index}`}>
              Selected ({row.count})
            </SelectedSectionHeader>
          );

        case 'selected-item':
          return (
            <SelectedItem
              key={`selected-${row.option.value}`}
              option={row.option}
              optionRenderer={optionRenderer}
              emptyValueLabel={emptyValueLabel}
              onClick={(e) => {
                e.stopPropagation();
                handleItemClick(row.option.value);
              }}
            />
          );

        case 'show-more-button':
          return (
            <ShowMoreButton
              key={`show-more-${index}`}
              onClick={(e) => {
                e.stopPropagation();
                setIsSelectedExpanded(!row.isExpanded);
              }}
            >
              {row.isExpanded
                ? 'Show less'
                : `Show all ${row.totalCount} selected`}
            </ShowMoreButton>
          );

        case 'divider':
          return <DividerStyled key={`divider-${index}`} />;

        case 'select-all':
          return (
            <SelectAllItem
              key={`select-all-${index}`}
              checked={row.checked}
              indeterminate={row.indeterminate}
              multiple={multiple}
              disabled={displayedOptions.length === 0}
              onClick={() => wrappedHandleItemClick('all')}
            />
          );

        case 'option':
          return (
            <OptionItem
              key={`option-${row.option.value}`}
              option={row.option}
              isSelected={row.isSelected}
              multiple={multiple}
              showCounter={showCounter}
              optionRenderer={optionRenderer}
              emptyValueLabel={emptyValueLabel}
              onClick={(e) => {
                e.stopPropagation();
                wrappedHandleItemClick(row.option.value);
                setInitialSort?.(false);
              }}
            />
          );

        default:
          return null;
      }
    },
    [
      handleItemClick,
      wrappedHandleItemClick,
      optionRenderer,
      emptyValueLabel,
      multiple,
      showCounter,
      setInitialSort,
      displayedOptions.length,
    ],
  );

  return (
    <PopoverStyled
      anchorEl={anchorElement}
      open={!!anchorElement}
      onClose={() => setAnchorElement(null)}
      anchorOrigin={{
        vertical: 35,
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          style: {
            maxHeight: '400px',
          },
        },
      }}
    >
      <Wrapper>
        {showSearch && (
          <SearchFieldContainer>
            <TextField
              placeholder="Search"
              variant="standard"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              fullWidth
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              autoFocus
              InputProps={{
                disableUnderline: true,
                sx: { px: 1 },
                startAdornment: (
                  <InputAdornment position="start">
                    {focused ? <SearchIconFocused /> : <SearchIcon />}
                  </InputAdornment>
                ),
              }}
            />
          </SearchFieldContainer>
        )}
        {virtualRows.length > 0 ? (
          <ScrollableContainer ref={scrollContainerRef}>
            {isReady && virtualItems.length > 0 ? (
              <Box
                sx={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualItems.map((virtualItem) => (
                  <VirtualItemContainer
                    key={virtualItem.key}
                    $height={virtualItem.size}
                    $offset={virtualItem.start}
                  >
                    {renderRow(
                      virtualRows[virtualItem.index],
                      virtualItem.index,
                    )}
                  </VirtualItemContainer>
                ))}
              </Box>
            ) : (
              <PlaceholderBox /> // Placeholder while initializing
            )}
          </ScrollableContainer>
        ) : (
          <NoOptionsText>No matching options.</NoOptionsText>
        )}
      </Wrapper>
    </PopoverStyled>
  );
};

export default OptionsMenu;
